import { Alert, LoadingPanel } from '@dispatch/ui';
import { useEffect, useState, type SyntheticEvent } from 'react';
import { apiRequest, errorMessage } from './api.js';
import type { BusinessSettings } from './types.js';

interface Props {
  businessId: string;
  csrfToken: string;
  supportAccessId?: string | undefined;
  canEdit: boolean;
}

interface SettingsForm {
  tradingName: string;
  timezone: string;
  countryCode: string;
  currencyCode: string;
  dateFormat: 'DD/MM/YYYY' | 'YYYY-MM-DD';
  timeFormat: '12h' | '24h';
  externalReferenceEnabled: boolean;
  externalReferenceLabel: string;
  jobNumberPrefix: string;
  jobNumberIncludeYear: boolean;
  jobNumberPadding: number;
}

function toForm(settings: BusinessSettings): SettingsForm {
  return {
    tradingName: settings.trading_name,
    timezone: settings.timezone,
    countryCode: settings.country_code,
    currencyCode: settings.currency_code,
    dateFormat: settings.date_format,
    timeFormat: settings.time_format,
    externalReferenceEnabled: settings.external_reference_enabled,
    externalReferenceLabel: settings.external_reference_label,
    jobNumberPrefix: settings.job_number_prefix,
    jobNumberIncludeYear: settings.job_number_include_year,
    jobNumberPadding: settings.job_number_padding,
  };
}

export function BusinessSettingsPanel({ businessId, csrfToken, supportAccessId, canEdit }: Props) {
  const [settings, setSettings] = useState<BusinessSettings>();
  const [form, setForm] = useState<SettingsForm>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(undefined);
    void apiRequest<BusinessSettings>(`/api/v1/businesses/${businessId}/settings`, {
      supportAccessId,
      signal: controller.signal,
    })
      .then((result) => {
        setSettings(result);
        setForm(toForm(result));
      })
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(errorMessage(caught));
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [businessId, supportAccessId]);

  async function save(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form || !canEdit) return;
    setSaving(true);
    setSaved(false);
    setError(undefined);
    try {
      const updated = await apiRequest<BusinessSettings>(
        `/api/v1/businesses/${businessId}/settings`,
        {
          method: 'PATCH',
          csrfToken,
          supportAccessId,
          body: form,
        },
      );
      setSettings(updated);
      setForm(toForm(updated));
      setSaved(true);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingPanel message="Loading business settings…" />;
  if (!form || !settings) {
    return (
      <Alert title="Business settings unavailable">{error ?? 'No settings were returned.'}</Alert>
    );
  }

  return (
    <section className="panel" aria-labelledby="business-settings-heading">
      <h2 id="business-settings-heading">Business settings</h2>
      <p className="lead">
        {settings.legal_name ?? form.tradingName}
        {settings.slug ? ` · ${settings.slug}` : ''}
      </p>
      {error ? <Alert title="Unable to save settings">{error}</Alert> : null}
      {saved ? <Alert tone="success">Business settings were saved.</Alert> : null}
      {!canEdit ? (
        <Alert tone="info">These settings are read-only for the current permission set.</Alert>
      ) : null}
      <form className="form-grid" onSubmit={(event) => void save(event)}>
        <div className="form-grid form-grid--two">
          <div className="form-field">
            <label htmlFor="trading-name">Trading name</label>
            <input
              id="trading-name"
              required
              maxLength={200}
              disabled={!canEdit}
              value={form.tradingName}
              onChange={(event) => setForm({ ...form, tradingName: event.target.value })}
            />
          </div>
          <div className="form-field">
            <label htmlFor="timezone">Timezone</label>
            <input
              id="timezone"
              required
              maxLength={100}
              disabled={!canEdit}
              value={form.timezone}
              onChange={(event) => setForm({ ...form, timezone: event.target.value })}
            />
          </div>
          <div className="form-field">
            <label htmlFor="country-code">Country code</label>
            <input
              id="country-code"
              required
              minLength={2}
              maxLength={2}
              disabled={!canEdit}
              value={form.countryCode}
              onChange={(event) =>
                setForm({ ...form, countryCode: event.target.value.toUpperCase() })
              }
            />
          </div>
          <div className="form-field">
            <label htmlFor="currency-code">Currency code</label>
            <input
              id="currency-code"
              required
              minLength={3}
              maxLength={3}
              disabled={!canEdit}
              value={form.currencyCode}
              onChange={(event) =>
                setForm({ ...form, currencyCode: event.target.value.toUpperCase() })
              }
            />
          </div>
          <div className="form-field">
            <label htmlFor="date-format">Date format</label>
            <select
              id="date-format"
              disabled={!canEdit}
              value={form.dateFormat}
              onChange={(event) =>
                setForm({ ...form, dateFormat: event.target.value as SettingsForm['dateFormat'] })
              }
            >
              <option value="DD/MM/YYYY">DD/MM/YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>
          <div className="form-field">
            <label htmlFor="time-format">Time format</label>
            <select
              id="time-format"
              disabled={!canEdit}
              value={form.timeFormat}
              onChange={(event) =>
                setForm({ ...form, timeFormat: event.target.value as SettingsForm['timeFormat'] })
              }
            >
              <option value="24h">24-hour</option>
              <option value="12h">12-hour</option>
            </select>
          </div>
        </div>

        <fieldset>
          <legend>External reference</legend>
          <div className="form-grid">
            <label className="checkbox-field">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={form.externalReferenceEnabled}
                onChange={(event) =>
                  setForm({ ...form, externalReferenceEnabled: event.target.checked })
                }
              />
              <span>Enable a business CRM, case or ticket reference.</span>
            </label>
            <div className="form-field">
              <label htmlFor="external-reference-label">Reference label</label>
              <input
                id="external-reference-label"
                required={form.externalReferenceEnabled}
                maxLength={100}
                disabled={!canEdit || !form.externalReferenceEnabled}
                value={form.externalReferenceLabel}
                onChange={(event) =>
                  setForm({ ...form, externalReferenceLabel: event.target.value })
                }
              />
            </div>
          </div>
        </fieldset>

        <fieldset>
          <legend>Job numbering foundation</legend>
          <div className="form-grid form-grid--two">
            <div className="form-field">
              <label htmlFor="job-prefix">Prefix</label>
              <input
                id="job-prefix"
                maxLength={20}
                disabled={!canEdit}
                value={form.jobNumberPrefix}
                onChange={(event) => setForm({ ...form, jobNumberPrefix: event.target.value })}
              />
            </div>
            <div className="form-field">
              <label htmlFor="job-padding">Number padding</label>
              <input
                id="job-padding"
                type="number"
                min={1}
                max={12}
                required
                disabled={!canEdit}
                value={form.jobNumberPadding}
                onChange={(event) =>
                  setForm({ ...form, jobNumberPadding: Number(event.target.value) })
                }
              />
            </div>
          </div>
          <label className="checkbox-field">
            <input
              type="checkbox"
              disabled={!canEdit}
              checked={form.jobNumberIncludeYear}
              onChange={(event) => setForm({ ...form, jobNumberIncludeYear: event.target.checked })}
            />
            <span>Include the year in generated job numbers.</span>
          </label>
        </fieldset>

        {canEdit ? (
          <div className="actions">
            <button type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save business settings'}
            </button>
          </div>
        ) : null}
      </form>
    </section>
  );
}
